// The mortuary (docs/plans/phase2-mortuary-and-notifications.md §3.2). Collector details,
// notes and who brought a body travel in request bodies only.
import { queryOptions } from '@tanstack/react-query';
import {
  mortuaryBoardSchema,
  mortuaryCaseSchema,
  mortuaryFormOptionsSchema,
  mortuarySettingsSchema,
  type createMortuarySlotSchema,
  type receiveBodySchema,
  type requestReleaseSchema,
  type updateMortuarySlotSchema,
} from '@wodi/shared';
import type { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

/** Refreshes every 30 seconds while the mortuary is open, like the bed board. */
export const mortuaryBoardQuery = queryOptions({
  queryKey: ['mortuary', 'board'],
  queryFn: () => api('GET', '/mortuary', { schema: mortuaryBoardSchema }),
  refetchInterval: 30_000,
});

export const mortuaryFormOptionsQuery = queryOptions({
  queryKey: ['mortuary', 'form-options'],
  queryFn: () => api('GET', '/mortuary/form-options', { schema: mortuaryFormOptionsSchema }),
  staleTime: 5 * 60_000,
});

export const mortuaryCaseQuery = (caseId: string) =>
  queryOptions({
    queryKey: ['mortuary', 'case', caseId],
    queryFn: () => api('GET', `/mortuary/cases/${caseId}`, { schema: mortuaryCaseSchema }),
  });

export const receiveBody = (body: In<typeof receiveBodySchema>) =>
  api('POST', '/mortuary/cases', { body, schema: mortuaryCaseSchema });

export const moveBody = (caseId: string, body: { slotId: string; reason?: string }) =>
  api('POST', `/mortuary/cases/${caseId}/moves`, { body, schema: mortuaryCaseSchema });

export const requestRelease = (caseId: string, body: In<typeof requestReleaseSchema>) =>
  api('POST', `/mortuary/cases/${caseId}/release-requests`, { body, schema: mortuaryCaseSchema });

export const decideRelease = (
  requestId: string,
  action: 'approve' | 'reject' | 'withdraw',
  note?: string,
) =>
  api('POST', `/mortuary/release-requests/${requestId}/${action}`, {
    body: note === undefined ? {} : { note },
    schema: mortuaryCaseSchema,
  });

export const cancelMortuaryCase = (caseId: string, reason: string) =>
  api('POST', `/mortuary/cases/${caseId}/cancel`, { body: { reason }, schema: mortuaryCaseSchema });

export const mortuarySettingsQuery = queryOptions({
  queryKey: ['mortuary', 'settings'],
  queryFn: () => api('GET', '/admin/mortuary', { schema: mortuarySettingsSchema }),
});

export const updateMortuarySettings = (storageServiceItemId: string) =>
  api('PUT', '/admin/mortuary', { body: { storageServiceItemId }, schema: mortuarySettingsSchema });

export const createMortuarySlot = (body: In<typeof createMortuarySlotSchema>) =>
  api('POST', '/admin/mortuary/slots', { body, schema: mortuarySettingsSchema });

export const updateMortuarySlot = (slotId: string, body: In<typeof updateMortuarySlotSchema>) =>
  api('PATCH', `/admin/mortuary-slots/${slotId}`, { body, schema: mortuarySettingsSchema });
